import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Bot, Camera, CloudSun, LayoutDashboard, RadioTower, SatelliteDish, Sparkles, Volume2, VolumeX } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { fetchNWSActive } from "../utils/nws";
import { SceneMonitor } from "../operations/SceneMonitor";
import { useStudioAuth } from "../operations/StudioAuthContext";
import { Kbd, OpsPage, Panel, StatusChip } from "../operations/ui";

const sceneIcons = { weatherfront: SatelliteDish, camera: Camera, forecast: CloudSun, live: RadioTower };
const isTyping = () => {
  const tag = document.activeElement?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
};

const OperationsDashboard = () => {
  const { request } = useStudioAuth();
  const [broadcast, setBroadcast] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [streams, setStreams] = useState([]);
  const [relay, setRelay] = useState(null);
  const [audio, setAudio] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [taking, setTaking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [broadcastResponse, scenesResponse, streamsResponse, relayResponse, audioResponse, nws] = await Promise.all([
        request("/broadcast"),
        request("/operations/scenes"),
        request("/streams"),
        request("/operations/relay"),
        request("/operations/audio"),
        fetchNWSActive(),
      ]);
      if (broadcastResponse.ok) setBroadcast(await broadcastResponse.json());
      if (scenesResponse.ok) setScenes(await scenesResponse.json());
      if (streamsResponse.ok) setStreams(await streamsResponse.json());
      if (relayResponse.ok) setRelay(await relayResponse.json());
      if (audioResponse.ok) setAudio((await audioResponse.json()).channels || []);
      setAlerts(nws || []);
      const automationResponse = await request("/operations/alerts/status");
      if (automationResponse.ok) setAutomation(await automationResponse.json());
    } catch (_) {
      toast.error("Unable to refresh Studio state");
    }
  }, [request]);

  useEffect(() => {
    load();
    const refresh = setInterval(load, 15000);
    const heartbeat = setInterval(() => request("/broadcast/heartbeat", { method: "POST" }).catch(() => {}), 8000);
    return () => { clearInterval(refresh); clearInterval(heartbeat); };
  }, [load, request]);

  const liveSource = streams.find((stream) => stream.live);
  const skycam = broadcast?.skycam_url ? { id: "program", name: broadcast.skycam_name || "HazCam Skycam", video_url: broadcast.skycam_url, poster: broadcast.skycam_url.replace("https://video.hazcams.com/", "https://data.hazcams.com/thumbnails/").replace("/index.m3u8", "/large.jpg") } : null;
  const lowerThird = { title: broadcast?.lower_third_title, subtitle: broadcast?.lower_third_subtitle };
  const severeAlert = useMemo(() => alerts.find((alert) => /tornado warning|severe thunderstorm warning|flash flood warning/i.test(alert.event)), [alerts]);
  const previewScene = scenes.find((scene) => scene.id === broadcast?.preview_scene);
  const programScene = scenes.find((scene) => scene.id === broadcast?.scene);
  const allMuted = audio.length > 0 && audio.every((channel) => channel.muted);

  const selectPreview = useCallback(async (sceneId) => {
    const response = await request("/broadcast", { method: "POST", body: JSON.stringify({ preview_scene: sceneId }) });
    if (response.ok) setBroadcast(await response.json());
  }, [request]);

  const take = useCallback(async () => {
    if (!broadcast?.preview_scene) return;
    setTaking(true);
    try {
      const response = await request("/operations/take", { method: "POST", body: JSON.stringify({ scene_id: broadcast.preview_scene }) });
      if (!response.ok) throw new Error();
      setBroadcast(await response.json());
      toast.success("Preview taken to Program");
    } catch (_) {
      toast.error("TAKE failed");
    } finally {
      setTaking(false);
    }
  }, [broadcast, request]);

  const toggleMasterMute = useCallback(async () => {
    if (!audio.length) return;
    const next = audio.map((channel) => ({ ...channel, muted: !allMuted }));
    setAudio(next);
    const response = await request("/operations/audio", { method: "POST", body: JSON.stringify({ channels: next }) });
    if (response.ok) toast.success(allMuted ? "All channels unmuted" : "All channels muted");
    else toast.error("Audio mix could not be saved");
  }, [audio, allMuted, request]);

  useEffect(() => {
    const onKey = (event) => {
      if (isTyping() || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.code === "Space") { event.preventDefault(); take(); return; }
      if (event.key.toLowerCase() === "m") { event.preventDefault(); toggleMasterMute(); return; }
      const index = Number(event.key);
      if (index >= 1 && index <= 9 && scenes[index - 1]) {
        event.preventDefault();
        selectPreview(scenes[index - 1].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scenes, take, toggleMasterMute, selectPreview]);

  const prepareStorm = async () => {
    if (!severeAlert) return;
    const response = await request("/broadcast", {
      method: "POST",
      body: JSON.stringify({
        preview_scene: "breaking_news",
        lower_third_title: severeAlert.event.toUpperCase(),
        lower_third_subtitle: severeAlert.areaDesc,
      }),
    });
    if (response.ok) {
      setBroadcast(await response.json());
      toast.success("Severe weather scene staged in Preview");
    }
  };

  return (
    <OpsPage
      icon={LayoutDashboard}
      title="Control Room"
      eyebrow="Preview → Program"
      actions={
        <>
          <StatusChip state={relay?.running ? "live" : "idle"} testId="relay-dashboard-status">{relay?.running ? "Output live" : "Relay offline"}</StatusChip>
          <StatusChip state={alerts.length ? "blocked" : "ok"} dot={false} testId="dashboard-alert-count">{alerts.length} NWS</StatusChip>
        </>
      }
    >
      <div className="grid gap-3 xl:grid-cols-[1fr_150px_1fr]">
        <SceneMonitor mode="preview" sceneId={broadcast?.preview_scene || "radar_full"} lowerThird={lowerThird} source={liveSource} skycam={skycam} />

        <div className="flex flex-col justify-center gap-2">
          <button
            data-testid="take-button"
            onClick={take}
            disabled={taking || !broadcast?.preview_scene}
            className="group flex h-16 items-center justify-center gap-3 border-2 border-red-500 bg-red-600 text-white transition-colors hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:border-white/10 disabled:bg-white/[0.04] disabled:text-slate-600 xl:h-28 xl:flex-col xl:gap-1"
          >
            <span className="display text-2xl font-black uppercase tracking-widest xl:text-3xl">{taking ? "Taking" : "Take"}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 xl:rotate-90 xl:group-hover:translate-x-0 xl:group-hover:translate-y-1" />
          </button>
          <div className="mono flex items-center justify-center gap-2 text-[9px] uppercase tracking-widest text-slate-600">
            <Kbd>Space</Kbd> cut
          </div>
          <div className="mono space-y-1 border border-[rgba(120,170,255,0.12)] bg-[#0a0f1d] p-2 text-[9px] uppercase tracking-widest">
            <div className="flex items-center justify-between gap-2"><span className="text-sky-400">PVW</span><span className="truncate text-slate-300">{previewScene?.name || "—"}</span></div>
            <div className="flex items-center justify-between gap-2"><span className="text-red-400">PGM</span><span className="truncate text-slate-300">{programScene?.name || "—"}</span></div>
          </div>
        </div>

        <SceneMonitor mode="program" sceneId={broadcast?.scene || "radar_full"} lowerThird={lowerThird} source={liveSource} skycam={skycam} />
      </div>

      <div className="mt-3 grid gap-3 xl:grid-cols-[1.5fr_.9fr]">
        <Panel title="Scene Bus" icon={Sparkles} note="Press 1–6 to stage" bodyClass="p-0">
          <div className="grid grid-cols-2 gap-px bg-[rgba(120,170,255,0.12)] sm:grid-cols-3 2xl:grid-cols-6">
            {scenes.map((scene, index) => {
              const Icon = sceneIcons[scene.source_type] || Sparkles;
              const staged = broadcast?.preview_scene === scene.id;
              const onAir = broadcast?.scene === scene.id;
              return (
                <button
                  key={scene.id}
                  onClick={() => selectPreview(scene.id)}
                  data-testid={`scene-select-${scene.id}`}
                  className={`relative min-h-[84px] bg-[#0a0f1d] p-3 text-left transition-colors hover:bg-white/[0.05] focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 ${staged ? "bg-sky-500/10" : ""}`}
                >
                  <span className={`absolute inset-x-0 bottom-0 h-0.5 ${onAir ? "bg-red-500" : staged ? "bg-sky-500" : "bg-transparent"}`} />
                  <div className="flex items-start justify-between gap-2">
                    <Icon className={`h-4 w-4 ${staged ? "text-sky-400" : "text-slate-500"}`} />
                    <span className="mono text-[9px] font-bold text-slate-600">{index + 1}</span>
                  </div>
                  <div className="display mt-2 truncate text-xs font-bold uppercase text-white">{scene.name}</div>
                  <div className="mono mt-0.5 text-[9px] uppercase tracking-widest text-slate-600">{onAir ? "On air" : staged ? "Staged" : scene.layout}</div>
                </button>
              );
            })}
          </div>
        </Panel>

        <div className="space-y-3">
          <Panel
            title="Assistant"
            icon={Bot}
            actions={severeAlert ? <StatusChip state="blocked" dot={false}>Action ready</StatusChip> : <StatusChip state="ok" dot={false}>Clear</StatusChip>}
            bodyClass="p-4"
          >
            {severeAlert ? (
              <>
                <p className="text-sm font-semibold leading-snug text-white">{severeAlert.event} · {severeAlert.areaDesc}</p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-400">{severeAlert.headline}</p>
                <button data-testid="prepare-storm-scene-button" onClick={prepareStorm} className="mono mt-3 inline-flex h-9 w-full items-center justify-center gap-2 border border-amber-400/30 bg-amber-400/10 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors hover:bg-amber-400/20">
                  <AlertTriangle className="h-4 w-4" /> Stage storm in Preview
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-400">No active severe warning requires scene preparation.</p>
            )}
            <div data-testid="dashboard-automation-status" className="mono mt-3 flex items-center justify-between gap-2 border-t border-[rgba(120,170,255,0.12)] pt-2 text-[9px] uppercase tracking-widest">
              <span className={automation?.auto_prep_enabled ? "text-emerald-400" : "text-slate-600"}>
                Auto prep {automation?.auto_prep_enabled ? "on" : "off"}
              </span>
              <span className="text-slate-600">{automation?.staged_today ?? 0} staged today</span>
            </div>
          </Panel>

          <Panel
            title="Master Audio"
            icon={allMuted ? VolumeX : Volume2}
            actions={
              <button data-testid="dashboard-master-mute-button" onClick={toggleMasterMute} className="mono h-7 border border-white/10 px-2 text-[9px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:bg-white/5 hover:text-white">
                {allMuted ? "Unmute" : "Mute"} <span className="ml-1 text-slate-600">M</span>
              </button>
            }
            bodyClass="p-3"
          >
            <div className="space-y-2">
              {audio.map((channel) => (
                <div key={channel.id} data-testid={`dashboard-audio-${channel.id}`} className="flex items-center gap-2">
                  <span className="mono w-16 shrink-0 truncate text-[9px] uppercase tracking-widest text-slate-400">{channel.name}</span>
                  <div className="h-1.5 flex-1 bg-white/[0.06]">
                    <div className={`h-full ${channel.muted ? "bg-slate-600" : "bg-sky-500"}`} style={{ width: `${channel.level}%` }} />
                  </div>
                  <span className="mono w-7 shrink-0 text-right text-[9px] tabular-nums text-slate-400">{channel.level}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Distribution" icon={RadioTower} bodyClass="p-0">
            <div className="grid grid-cols-2 gap-px bg-[rgba(120,170,255,0.12)]">
              {(relay?.destinations || []).map((destination) => (
                <div key={destination.key} data-testid={`dashboard-destination-${destination.key}`} className="bg-[#0a0f1d] px-3 py-2.5">
                  <div className="truncate text-xs font-bold text-white">{destination.name}</div>
                  <div className={`mono mt-1 text-[9px] uppercase tracking-widest ${destination.configured ? "text-sky-300" : "text-slate-600"}`}>
                    {destination.configured ? "Ready" : "Credentials required"}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </OpsPage>
  );
};

export default OperationsDashboard;
